
import React, { useState, useMemo } from 'react';
import { Filter, ChevronDown, Plus, Truck, Undo2, X, MapPin, ChevronRight, Pencil, Trash2, Printer, ArrowLeft, Info, FileText, CheckCircle2, Building2, UserCircle, Phone, Mail, Calendar, Clock, Hash, Loader2, ArrowRightLeft } from 'lucide-react';
import { OrderStatus, Priority, Client, Order, MaterialItem, Team, UserRole } from './types';
import { STATUS_COLORS } from './constants';
import { Card, Badge, TabButton, OrderTimeline } from './SharedComponents';

export const OrdersPage = ({ orders, clients, companies, onSelectOrder, onNewOrder, user }: any) => {
  const [statusFilter, setStatusFilter] = useState('TODOS');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [printingOrder, setPrintingOrder] = useState<Order | null>(null);

  const filteredOrders = useMemo(() => {
    const operationalOrders = orders.filter((o: Order) => o.status !== OrderStatus.AGUARDANDO_FINANCEIRO);
    if (statusFilter === 'TODOS') return operationalOrders;
    return operationalOrders.filter((o: Order) => o.status === statusFilter);
  }, [orders, statusFilter]);

  if (printingOrder) {
    return <OrderPrintView order={printingOrder} clients={clients} companies={companies} user={user} onClose={() => setPrintingOrder(null)} />;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 p-4 md:p-8 lg:p-10 text-secondary">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tighter leading-none">Ordens de Serviço</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic">Gestão operacional de montagens e entregas.</p>
        </div>
        
        <div className="flex flex-row items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:flex-none">
            <button 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full bg-white border border-gray-100 px-5 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-gray-50 transition-all shadow-sm"
            >
              <Filter size={14} className="text-primary" /> {statusFilter} <ChevronDown size={12} className={`text-gray-300 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
            </button>
            {isFilterOpen && (
              <div className="absolute right-0 mt-3 w-full md:w-64 bg-white border border-gray-100 rounded-3xl shadow-2xl z-60 overflow-hidden p-2 animate-in slide-in-from-top-2">
                <button 
                  onClick={() => { setStatusFilter('TODOS'); setIsFilterOpen(false); }} 
                  className="w-full text-left px-5 py-4 text-[10px] font-black uppercase hover:bg-primary/5 hover:text-primary rounded-2xl transition-all"
                >
                  Todos os Status
                </button>
                {Object.values(OrderStatus).filter(s => s !== OrderStatus.AGUARDANDO_FINANCEIRO).map(s => (
                  <button 
                    key={s} 
                    onClick={() => { setStatusFilter(s); setIsFilterOpen(false); }} 
                    className="w-full text-left px-5 py-4 text-[10px] font-black uppercase hover:bg-primary/5 hover:text-primary rounded-2xl transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onNewOrder} className="flex-1 md:flex-none bg-primary text-white px-8 py-3.5 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:brightness-110 transition-all shadow-xl shadow-primary/20 active:scale-95">
            <Plus size={18} strokeWidth={3} /> Nova OS
          </button>
        </div>
      </div>

      <Card className="hidden md:block overflow-hidden border-none shadow-xl bg-white rounded-[32px]">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr className="bg-gray-50/50 text-gray-400 text-[9px] uppercase font-black tracking-[0.2em] border-b border-gray-100">
                <th className="px-8 py-6">Evento / Referência</th>
                <th className="px-8 py-6">Datas de Campo</th>
                <th className="px-8 py-6">Equipe</th>
                <th className="px-8 py-6">Status Operacional</th>
                <th className="px-8 py-6 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredOrders.length > 0 ? filteredOrders.map((o: Order) => (
                <tr key={o.id} className="hover:bg-primary/[0.02] transition-colors group cursor-default">
                  <td className="px-8 py-7">
                    <p className="font-black uppercase tracking-tight text-sm">{o.nomeEvento}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">ID: {o.id}</p>
                  </td>
                  <td className="px-8 py-7">
                    <div className="flex flex-col gap-1.5 text-[11px] font-black uppercase text-secondary">
                      <span className="flex items-center gap-2"><Truck size={14} className="text-primary" /> {o.dataEvento}</span>
                      <span className="flex items-center gap-2 opacity-30 italic"><Undo2 size={14} /> {o.diaRetirada}</span>
                    </div>
                  </td>
                  <td className="px-8 py-7">
                    <Badge className="bg-secondary/5 text-secondary border-none px-3 py-1 font-black text-[9px] tracking-widest">
                      {o.teamId ? `LOGÍSTICA ATRIBUÍDA` : 'FILA DE DESPACHO'}
                    </Badge>
                  </td>
                  <td className="px-8 py-7">
                    <Badge className={`${STATUS_COLORS[o.status as OrderStatus]} px-4 py-1.5 text-[9px] font-black tracking-widest border-none shadow-sm`}>
                      {o.status}
                    </Badge>
                  </td>
                  <td className="px-8 py-7 text-right">
                    <div className="flex items-center justify-end gap-2">
                       <button 
                        onClick={() => setPrintingOrder(o)}
                        className="p-3.5 text-gray-300 hover:text-secondary hover:bg-gray-100 rounded-2xl transition-all shadow-sm bg-white border border-gray-100 active:scale-90"
                        title="Visualizar para Impressão"
                       >
                         <Printer size={20} strokeWidth={2.5} />
                       </button>
                       <button 
                        onClick={() => onSelectOrder(o)} 
                        className="p-3.5 text-gray-300 hover:text-primary hover:bg-primary/5 rounded-2xl transition-all shadow-sm bg-white border border-gray-100 active:scale-90"
                       >
                        <ChevronRight size={20} strokeWidth={3} />
                       </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="px-8 py-32 text-center text-gray-200 font-black uppercase tracking-[0.3em] text-xs italic">Nenhuma ordem ativa</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export const OrderDetailsPanel = ({ order, clients, teams, onClose, onUpdateStatus, onAssignTeam, onEdit, user }: any) => {
  const [activeTab, setActiveTab] = useState('details');
  const client = clients.find((c: Client) => c.id === order.clientId) || { empresa: "N/A", nomeLocal: "N/A", enderecoCompleto: "---" };

  return (
    <div className="fixed inset-0 bg-secondary/70 backdrop-blur-md z-[100] flex justify-end">
      <div className="w-full md:max-w-2xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 overflow-hidden">
        <div className="p-8 md:p-10 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10 shrink-0">
          <div className="flex justify-between items-start mb-6">
             <Badge className={`px-5 py-2 text-[10px] font-black border-none shadow-lg ${STATUS_COLORS[order.status as OrderStatus]}`}>{order.status}</Badge>
             <button onClick={onClose} className="p-3 bg-white border border-gray-100 hover:bg-red-50 hover:text-red-500 rounded-2xl text-gray-300 transition-all shadow-sm">
                <X size={20} strokeWidth={3} />
             </button>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-secondary tracking-tighter leading-[0.9] uppercase italic">{order.nomeEvento}</h2>
          <div className="mt-4 flex items-center gap-2 text-primary font-black text-[10px] md:text-xs uppercase tracking-[0.2em] italic">
             <MapPin size={14} strokeWidth={3} /> {client.nomeLocal}
          </div>
          <div className="mt-10"><OrderTimeline currentStatus={order.status} /></div>
        </div>

        <div className="flex border-b border-gray-100 px-6 md:px-10 bg-white overflow-x-auto scrollbar-hide shrink-0">
          <TabButton active={activeTab === 'details'} onClick={() => setActiveTab('details')}>Logística</TabButton>
          <TabButton active={activeTab === 'pod'} onClick={() => setActiveTab('pod')}>Protocolo</TabButton>
          <TabButton active={activeTab === 'materials'} onClick={() => setActiveTab('materials')}>Checklist</TabButton>
        </div>

        <div className="flex-1 overflow-y-auto p-8 md:p-10 space-y-12 scrollbar-hide pb-32">
          {activeTab === 'details' && (
            <div className="space-y-12 animate-in fade-in">
              <section className="space-y-6">
                 <div className="flex justify-between items-end border-b border-gray-50 pb-3">
                    <h4 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] flex items-center gap-3 italic">
                      Squad de Campo
                    </h4>
                    {order.teamId && (
                      <span className="text-[8px] font-black text-amber-600 uppercase flex items-center gap-1">
                        <ArrowRightLeft size={10} /> Transferência Habilitada
                      </span>
                    )}
                 </div>
                 
                 <div className="grid grid-cols-1 gap-4">
                   {teams.map((t: Team) => {
                     const isCurrent = order.teamId === t.id;
                     return (
                       <button 
                         key={t.id}
                         onClick={() => onAssignTeam(order.id, t.id)}
                         className={`p-5 rounded-[28px] border-2 transition-all flex items-center justify-between group ${isCurrent ? 'bg-primary border-primary text-white shadow-2xl shadow-primary/20' : 'bg-gray-50 border-gray-100 text-secondary hover:border-primary/20'}`}
                       >
                          <div className="flex items-center gap-5 text-left">
                             <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg italic shadow-sm ${isCurrent ? 'bg-white/20' : 'bg-white text-primary'}`}>{t.name.charAt(t.name.length - 1)}</div>
                             <div>
                                <p className="font-black uppercase tracking-tight text-base">{t.name}</p>
                                <p className={`text-[8px] font-bold uppercase tracking-widest mt-1 opacity-60`}>
                                   {isCurrent ? 'SQUAD RESPONSÁVEL ATUAL' : 'CLIQUE PARA REESCALONAR'}
                                </p>
                             </div>
                          </div>
                          {isCurrent ? (
                             <div className="p-2 bg-white/20 rounded-full animate-in zoom-in"><CheckCircle2 size={20} strokeWidth={4} /></div>
                          ) : (
                             <div className="p-2 bg-primary/10 text-primary rounded-full opacity-0 group-hover:opacity-100 transition-all">
                                <ArrowRightLeft size={18} strokeWidth={3} />
                             </div>
                          )}
                       </button>
                     );
                   })}
                 </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderPrintView = ({ order, clients, companies, user, onClose }: { order: Order, clients: Client[], companies: any[], user: any, onClose: () => void }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const client = clients.find(c => c.id === order.clientId);
  const rentalCompany = companies.find(c => c.id === user.companyId) || companies[0] || { name: 'Empresa' };

  const documentHTML = `
    <style>
      @page { size: A4; margin: 5mm; }
      body { font-family: 'Inter', Helvetica, Arial, sans-serif; font-size: 8.5pt; color: #000; line-height: 1.25; margin: 0; padding: 0; background: white; }
      .doc-container { width: 190mm; margin: 0 auto; padding: 5mm; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 0; table-layout: fixed; }
      td, th { border: 1px solid #000; padding: 6px; vertical-align: top; word-wrap: break-word; }
      
      .header-row td { border: none; }
      .logo-cell { width: 35%; text-align: left; vertical-align: middle; }
      .info-cell { width: 65%; text-align: right; font-weight: bold; }
      .logo-img { max-width: 140px; height: auto; object-fit: contain; display: block; }
      
      .doc-title { text-align: center; font-size: 16pt; font-weight: 900; margin: 5mm 0; text-transform: uppercase; border-top: 3px solid #000; border-bottom: 3px solid #000; padding: 6px 0; letter-spacing: -0.01em; }
      
      .label { font-weight: 900; text-transform: uppercase; font-size: 7.5pt; width: 30mm; display: inline-block; color: #000; }
      .value-strong { font-weight: 900; text-transform: uppercase; font-size: 9.5pt; }
      
      .items-table th { background: #f2f2f2; text-transform: uppercase; font-size: 8pt; text-align: left; font-weight: 900; }
      .center { text-align: center; }
      .col-qty { width: 50px; }
      .col-med { width: 50px; }
      
      .obs-box { border: 1px solid #000; margin-top: 5mm; padding: 12px; font-size: 8.5pt; background: #fff; }
      .obs-title { font-weight: 900; text-decoration: underline; margin-bottom: 3mm; display: block; font-size: 9.5pt; }
      .alert-line { font-weight: 900; margin-bottom: 3px; display: block; }
      
      .footer-stamp { display: flex; justify-content: space-between; margin-top: 6mm; font-size: 7.5pt; color: #555; border-top: 1.5px solid #000; padding-top: 3mm; font-weight: 600; }
      .highlight-row { background-color: #fcfcfc; }
      .red-alert { color: #d90000; font-weight: 900; }
    </style>
    <div class="doc-container">
      <table>
        <tr class="header-row">
          <td class="logo-cell">
            <img src="https://i.postimg.cc/mD8V1G1w/eventlog-logo.png" class="logo-img" alt="EventLog Logo" />
          </td>
          <td class="info-cell">
            SOROCABA, ${new Date().toLocaleDateString('pt-BR')}<br/>
            VALIDADE PROPOSTA: ${order.validadeProposta || '---'}
          </td>
        </tr>
      </table>

      <div class="doc-title">Ordem de Serviço Operacional nº ${order.id.replace('ord-', '')}</div>

      <table class="highlight-row">
        <tr>
          <td colspan="2"><span class="label">LOCADORA:</span> <span class="value-strong">${rentalCompany.name}</span></td>
          <td><span class="label">FANTASIA:</span> <span class="value-strong">${rentalCompany.name}</span></td>
        </tr>
        <tr>
          <td><span class="label">CNPJ:</span> ${rentalCompany.cnpj}<br/><span class="label">E-MAIL:</span> ${rentalCompany.contactEmail}</td>
          <td colspan="2"><span class="label">CONTATO:</span> ${rentalCompany.whatsapp}<br/><span class="label">ENDEREÇO:</span> ${rentalCompany.address}, ${rentalCompany.neighborhood}</td>
        </tr>
      </table>

      <table style="margin-top: -1px;">
        <tr class="highlight-row"><td colspan="3"><span class="label">LOCATÁRIO(A):</span> <span class="value-strong">${client?.empresa}</span></td></tr>
        <tr>
          <td style="width: 40%;"><span class="label">CNPJ/CPF:</span> ${client?.cnpj_cpf || '---'}<br/><span class="label">E-MAIL:</span> ${client?.emailContato}</td>
          <td colspan="2"><span class="label">TELEFONE:</span> ${client?.telefonePrincipal}<br/><span class="label">ENDEREÇO:</span> ${client?.enderecoCompleto}</td>
        </tr>
      </table>

      <table style="margin-top: -1px;">
        <tr class="highlight-row">
          <td style="width: 50%;">
            <span class="label">DESC. EVENTO:</span> <span class="value-strong">${order.nomeEvento}</span><br/>
            <span class="label">DATA EVENTO:</span> <span class="value-strong">${order.dataEvento} ATÉ ${order.diaRetirada}</span><br/>
            <span class="label">DATA ENTREGA:</span> <span class="value-strong">${order.dataEvento} ÀS ${order.horarioEntrega}</span>
          </td>
          <td style="width: 50%;">
            <span class="label">LOCAL:</span> <span class="value-strong">${client?.nomeLocal}</span><br/>
            <span class="label">RESPONSÁVEL:</span> ${client?.contatoResponsavel}<br/>
            <span class="label">RETIRADA:</span> <span class="red-alert" style="font-size: 11pt;">${order.diaRetirada} ÀS ${order.horarioRetirada}</span>
          </td>
        </tr>
        <tr class="highlight-row">
            <td colspan="2">
                <span class="label">VENDEDOR:</span> <span class="value-strong">${order.vendedorName || 'CENTRAL'}</span><br/>
                <span class="label">REF. INTERNA:</span> ${order.id}
            </td>
        </tr>
      </table>

      <div style="background: #000; color: #fff; text-align: center; padding: 5px; font-weight: 900; font-size: 9pt; text-transform: uppercase; margin-top: 4mm;">
        LISTAGEM DE ROMANEIO (CONFERÊNCIA DE CAMPO)
      </div>
      <table class="items-table" style="margin-top: -1px;">
        <thead>
          <tr>
            <th style="width: 55%;">Equipamento / Descrição Técnica</th>
            <th class="col-qty center">Qtd.</th>
            <th class="col-med center">Med.</th>
            <th style="width: 45mm;">Observações de Campo</th>
          </tr>
        </thead>
        <tbody>
          ${order.materials.map(m => `
            <tr>
              <td style="font-weight: 900; text-transform: uppercase; font-size: 9pt;">${m.name}</td>
              <td class="center" style="font-weight: 900; font-size: 12pt;">${m.qty}</td>
              <td class="center uppercase" style="font-weight: 700;">${m.medida || 'un'}</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div style="border: 2px solid #000; padding: 12px; text-align: center; margin-top: -1px; font-weight: 900; background: #fdfdfd; font-size: 8.5pt; color: #222; text-transform: uppercase;">
        DOCUMENTO ESTRITAMENTE OPERACIONAL. VALORES FINANCEIROS SOB SIGILO PARA LOGÍSTICA.<br/>
        CONFERIR APENAS QUANTIDADES E INTEGRIDADE DOS MATERIAIS.
      </div>

      <div class="obs-box">
        <span class="obs-title">NORMAS E CONDIÇÕES LOGÍSTICAS:</span>
        <span class="alert-line">⚠️ RESERVA CONFIRMADA MEDIANTE ASSINATURA DE CONTRATO E PAGAMENTO DO SINAL.</span>
        <span class="alert-line">⚠️ CONFERÊNCIA OBRIGATÓRIA NO ATO DA ENTREGA. NÃO ACEITAMOS RECLAMAÇÕES POSTERIORES.</span>
        <span class="alert-line">⚠️ A MONTAGEM É EXCLUSIVA PARA O LOCAL DESIGNADO NO DESCARREGAMENTO (MAX. 10 METROS DO VEÍCULO).</span>
        <span class="alert-line">⚠️ DEVOLVA O MATERIAL NAS MESMAS CONDIÇÕES RECEBIDAS. AVARIAS SERÃO COBRADAS SEPARADAMENTE.</span>
        <br/>
        <span class="alert-line">Bom evento!<br/><strong>LOGÍSTICA - ${rentalCompany.name.toUpperCase()}</strong></span>
      </div>

      <div class="footer-stamp">
        <div>SISTEMA EVENTLOG PRO v4.2 | EMISSÃO AUTOMÁTICA EM: ${new Date().toLocaleString('pt-BR')}</div>
        <div>PROTOCOLO: ${order.id} | PÁGINA 1 DE 1</div>
      </div>
    </div>
  `;

  const handlePrint = () => {
    setIsGenerating(true);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>OS #${order.id}</title>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap" rel="stylesheet">
          </head>
          <body>
            ${documentHTML}
            <script>window.onload=function(){setTimeout(function(){window.print();window.close();}, 500);}</script>
          </body>
        </html>
      `);
      printWindow.document.close();
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#080c14] z-[2000] flex flex-col p-4 md:p-10 animate-in fade-in overflow-hidden">
       {/* Barra de Ações Superior */}
       <div className="max-w-6xl mx-auto w-full flex flex-col md:flex-row justify-between items-center mb-8 shrink-0 gap-4">
          <button onClick={onClose} className="flex items-center gap-3 text-white/40 hover:text-white transition-all font-black uppercase text-[10px] tracking-widest bg-white/5 px-6 py-4 rounded-[20px] border border-white/5">
            <ArrowLeft size={20} /> Voltar para Operação
          </button>
          <div className="flex gap-4 w-full md:w-auto">
             <button 
               onClick={handlePrint} 
               disabled={isGenerating} 
               className="flex-1 md:flex-none bg-primary text-white px-12 py-5 rounded-[24px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3"
             >
                {isGenerating ? <Loader2 size={22} className="animate-spin" /> : <Printer size={22} strokeWidth={3} />}
                Imprimir Documento Oficial
             </button>
             <button onClick={onClose} className="p-5 bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-500 rounded-[24px] transition-all border border-white/5">
                <X size={28} />
             </button>
          </div>
       </div>

       {/* Área de Visualização (Preview Real) */}
       <div className="flex-1 overflow-y-auto pb-20 scrollbar-hide px-4">
          <div className="w-full max-w-[210mm] bg-white mx-auto shadow-[0_60px_120px_rgba(0,0,0,0.6)] rounded-sm transform scale-90 md:scale-100 origin-top overflow-hidden border-8 border-white min-h-[297mm]">
             <div dangerouslySetInnerHTML={{ __html: documentHTML }} />
          </div>
       </div>

       {/* Overlay de Status Informativo */}
       <div className="fixed bottom-10 right-10 bg-primary text-white px-10 py-5 rounded-[30px] font-black uppercase text-[11px] tracking-[0.3em] shadow-2xl flex items-center gap-4 animate-bounce z-[2100]">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse shadow-[0_0_15px_rgba(255,255,255,1)]"></div>
          Modo Visualização A4
       </div>
    </div>
  );
};
